import {Box} from '@mui/material'
import React, {ReactNode, useCallback} from 'react'

import {useMapZoomPan} from '../../hooks'
import {Point} from '../../types'

import {MapZoomControls} from './MapZoomControls'

type ZoomPosFunction = (pos: Point) => Point

type ZoomableMapContainerProps = {
  width: number
  height: number
  zoomableContent?: ReactNode
  renderMarkers?: (getZoomedPosition: ZoomPosFunction) => ReactNode
  minZoom?: number
  maxZoom?: number
  zoomStep?: number
  initialZoom?: number
  initialPanX?: number
  initialPanY?: number
  enableZoom?: boolean
  showControls?: boolean
}

export const ZoomableMapContainer: React.FC<ZoomableMapContainerProps> = ({
  width,
  height,
  zoomableContent,
  renderMarkers,
  minZoom = 1,
  maxZoom = 4,
  zoomStep = 0.15,
  initialZoom,
  initialPanX = 0,
  initialPanY = 0,
  enableZoom = true,
  showControls = true
}) => {
  const {
    zoom,
    panX,
    panY,
    containerRef,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetView,
    zoomIn,
    zoomOut,
    isDragging
  } = useMapZoomPan({
    minZoom: enableZoom ? minZoom : 1,
    maxZoom: enableZoom ? maxZoom : 1,
    zoomStep,
    initialZoom: enableZoom ? (initialZoom ?? minZoom) : 1,
    initialPanX: enableZoom ? initialPanX : 0,
    initialPanY: enableZoom ? initialPanY : 0
  })

  const getZoomedPosition = useCallback(
    (position: Point) => ({
      x: position.x * zoom + panX,
      y: position.y * zoom + panY
    }),
    [zoom, panX, panY]
  )

  return (
    <Box sx={{width, height, position: 'relative'}}>
      <Box
        ref={containerRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          width: '100%',
          height: '100%',
          cursor: isDragging ? 'grabbing' : zoom > 1 ? 'grab' : 'default',
          overflow: 'hidden',
          position: 'relative',
          touchAction: zoom > 1 ? 'none' : 'auto'
        }}
      >
        <Box
          sx={{
            transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
            transformOrigin: '0 0',
            willChange: 'transform',
            width,
            height,
            position: 'absolute'
          }}
        >
          {zoomableContent}
        </Box>

        {renderMarkers?.(getZoomedPosition)}
      </Box>

      {enableZoom && showControls && (
        <MapZoomControls
          zoom={zoom}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onReset={resetView}
          minZoom={minZoom}
          maxZoom={maxZoom}
        />
      )}
    </Box>
  )
}
